import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    HeaderFilter,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

const numberFormat = { type: "fixedPoint", precision: 2 };

/**
 * Reusable WM transaction-charges grid backed by
 * wm/TransactionCharge/GetTransactionChargeDetails (header rowid sent via the
 * `Key` request header). `chargeType` (default "T" = Transaction Charges) filters
 * the returned N/T/M charges client-side.
 */
const CustomerTransactionCharges = (props) => {
    const { transactionId, chargeType = "T", gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);
    const [rows, setRows] = useState([]);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const allowedPageSizes = [10, 20, 100, 500, "all"];
    const stateKey = gridKey;
    const loadState = useCallback(() => GridLayout.sendStorageRequest(stateKey, "GET", accessToken, null, currentLayout), [stateKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(stateKey, "POST", accessToken, state, currentLayout), [stateKey, currentLayout]);

    useEffect(() => {
        if (!transactionId) {
            setRows([]);
            return;
        }
        axios.get(`${window._env_.REACT_APP_APIS}/${data.Transaction.GetTransactionChargeDetails}`, {
            headers: {
                Key: transactionId,
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((res) => {
            let list = Array.isArray(res.data) ? res.data : [];
            if (chargeType) {
                list = list.filter((r) => r.chargetype === chargeType);
            }
            setRows(list);
        }).catch((err) => {
            console.error("Failed to load transaction charges:", err);
            setRows([]);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionId, chargeType]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            dataSource={rows}
            keyExpr="rowid"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={stateKey} customSave={saveState} />

            {/* Default-visible columns (mirrors the AI8 charges layout) */}
            <Column dataField="chargeCode" caption="Charge" />
            <Column dataField="description" caption="Description" />
            <Column dataField="quantitybillas" caption="Bill As" dataType="number" format={numberFormat} />
            <Column dataField="quantitybillasuom" caption="UOM" />
            <Column dataField="rate" caption="Rate" dataType="number" format={numberFormat} />
            <Column dataField="amount" caption="Amount" dataType="number" format="$ #,##0.00" />

            {/* Column-chooser (hidden by default) */}
            <Column dataField="chargetiming" caption="Charge Timing" visible={false} />
            <Column dataField="userdefinedcode1" caption="Code 1" visible={false} />
            <Column dataField="userdefinedcode2" caption="Code 2" visible={false} />
            <Column dataField="userdefinedcode3" caption="Code 3" visible={false} />
            <Column dataField="userdefinedcode4" caption="Code 4" visible={false} />
            <Column dataField="userdefinedcode5" caption="Code 5" visible={false} />
            <Column dataField="companyid" caption="Company ID" visible={false} />
            <Column dataField="creationuserid" caption="Created By" visible={false} />
            <Column dataField="creationdatetime" caption="Creation Date" dataType="date" visible={false} />
            <Column dataField="userdefineddate1" caption="Date 1" dataType="date" visible={false} />
            <Column dataField="userdefineddate2" caption="Date 2" dataType="date" visible={false} />
            <Column dataField="userdefineddate3" caption="Date 3" dataType="date" visible={false} />
            <Column dataField="userdefineddate4" caption="Date 4" dataType="date" visible={false} />
            <Column dataField="userdefineddate5" caption="Date 5" dataType="date" visible={false} />
            <Column dataField="lastupdatedatetime" caption="Last Updated" dataType="date" visible={false} />
            <Column dataField="lastupdateuserid" caption="Last Updated By" visible={false} />
            <Column dataField="module" caption="Module" visible={false} />
            <Column dataField="userdefinedquantity1" caption="Quantity 1" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity2" caption="Quantity 2" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity3" caption="Quantity 3" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity4" caption="Quantity 4" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity5" caption="Quantity 5" dataType="number" visible={false} />
            <Column dataField="recordNumber" caption="Record #" visible={false} />
            <Column dataField="recordtype" caption="Record Type" visible={false} />
            <Column dataField="chargeReleaseDate" caption="Release Date" dataType="date" visible={false} />
            <Column dataField="sequence" caption="Sequence" dataType="number" visible={false} />
            <Column dataField="status" caption="Status" visible={false} />
            <Column dataField="chargetype" caption="Type" visible={false} />
            <Toolbar>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CustomerTransactionCharges);
